import clsx from "clsx";
import React from "react";

const Button = ({
  children,
  onClick,
  type = "button",
  color = "blue",
  variant = "filled", // filled | outlined | text | link
  size = "md", // sm | md | lg | xs | xl
  disabled = false,
  loading = false,
  iconLeft = null,
  iconRight = null,
  icon = null,
  className = "",
  labelStyle = "",
  label = "",
  tooltip = "", // Tooltip text
}) => {
  const handleClick = (e) => {
    if (disabled || loading) return;
    if (onClick) onClick(e);
  };

  const baseStyles =
    "overflow-hidden flex items-center justify-center font-medium rounded-lg transition duration-300 ease-in-out focus:outline-none";

  const sizeStyles = {
    xs: "px-2 py-1 text-xs",
    sm: "px-3 py-1 text-sm",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-lg",
    xl: "px-8 py-4 text-xl",
  };

  const colorStyles = {
    blue: "bg-blue-500 text-white hover:bg-blue-600",
    red: "bg-red-500 text-white hover:bg-red-600",
    green: "bg-green-500 text-white hover:bg-green-600",
    orange: "bg-[#FF602B] text-white hover:bg-[#FF602B]",
    gray: "bg-gray-600 text-white hover:bg-gray-700 dark:bg-gray-800 dark:hover:bg-gray-900",
    white:
      "bg-white text-gray-800 hover:bg-gray-50 dark:bg-dark-surface dark:text-white dark:hover:bg-gray-700",
    yellow: "bg-yellow-500 text-white hover:bg-yellow-600",
    coral: "bg-[#FF7F50] text-white hover:bg-[#FF6333]",
  };

  const outlineStyles = {
    blue: "border border-blue-500 text-blue-500 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-700",
    red: "border border-red-500 text-red-500 hover:bg-red-100 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-700",
    green:
      "border border-green-500 text-green-500 hover:bg-green-100 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-700",
    gray: "border border-gray-500 text-gray-500 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-gray-700",
    white: "border text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-700 dark:border-none",
    yellow:
      "border border-yellow-500 text-yellow-500 hover:bg-yellow-100 dark:border-yellow-800 dark:text-yellow-400 dark:hover:bg-yellow-700",
  };

  const linkStyles = {
    blue: "text-blue-500 !p-0 hover:underline rounded-none",
    red: "text-red-500 !p-0 hover:underline rounded-none",
    green: "text-green-500 !p-0 hover:underline rounded-none",
    gray: "text-gray-500 !p-0 hover:underline rounded-none",
    white: "text-gray-800 dark:text-white !p-0 hover:underline rounded-none",
    yellow: "text-yellow-500 !p-0 hover:underline rounded-none",
  };

  const textStyles = {
    blue: "text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-700",
    red: "text-red-500 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-700",
    green: "text-green-500 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-700",
    gray: "text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700",
    white: "text-gray-800 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-700",
    yellow: "text-yellow-500 hover:bg-yellow-100 dark:text-yellow-400 dark:hover:bg-yellow-700",
  };

  const spinnerSize =
    size === "xs"
      ? "w-4 h-4"
      : size === "sm"
        ? "w-5 h-5"
        : size === "md"
          ? "w-[22px] h-[22px]"
          : size === "lg"
            ? "w-[26px] h-[26px]"
            : "w-10 h-10";

  const isIconOnly = !children && !label && (icon || iconLeft || iconRight);

  const buttonContent = (
    <button
      className={clsx(
        baseStyles,
        sizeStyles[size],
        variant === "filled" && colorStyles[color],
        variant === "outlined" && outlineStyles[color],
        variant === "text" && textStyles[color],
        variant === "link" && linkStyles[color],
        (disabled || loading) && "cursor-not-allowed opacity-50",
        isIconOnly && "!p-1",
        className
      )}
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <span
          className={`${spinnerSize} animate-spin rounded-full border-2 border-white border-t-transparent`}
        ></span>
      ) : (
        <>
          {isIconOnly ? (
            <span>{icon}</span>
          ) : (
            <>
              {iconLeft && <span>{iconLeft}</span>}
              {label && (
                <span
                  className={`${labelStyle} ${variant == "link" ? "mx-0 text-base" : "mx-2"} font-semibold leading-normal`}
                >
                  {label}
                </span>
              )}
              {children && <span>{children}</span>}
              {iconRight && <span>{iconRight}</span>}
            </>
          )}
        </>
      )}
    </button>
  );

  return tooltip ? (
    <div className="group relative">
      {buttonContent}
      <span className="absolute -top-8 left-1/2 w-max -translate-x-1/2 scale-0 rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100">
        {tooltip}
      </span>
    </div>
  ) : (
    buttonContent
  );
};

export default Button;
